import { useMemo, useState } from 'react';
import { GoogleMap, OverlayView, OVERLAY_MOUSE_TARGET, useLoadScript } from '@react-google-maps/api';
import { MapPin } from 'lucide-react';
import { GOOGLE_MAPS_API_KEY } from '../../config/maps';

const MAP_CONTAINER_STYLE = { width: '100%', height: '600px', borderRadius: '12px' };
// Nairobi — fallback center when nothing on screen has a pin yet.
const DEFAULT_CENTER = { lat: -1.286389, lng: 36.817223 };

// Abbreviated for a map pill, where a full "KSh 25,000" would be too wide — same
// rounding convention the FindRental list cards use.
const formatMarkerPrice = (amount) => {
  if (amount === null || amount === undefined) return 'POA';
  if (amount >= 1000000) return `KSh ${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `KSh ${Math.round(amount / 1000)}K`;
  return `KSh ${amount}`;
};

// Centers the pill horizontally on its coordinate, and sits it just above the point
// rather than covering it.
const getPixelPositionOffset = (width, height) => ({
  x: -(width / 2),
  y: -height - 6,
});

/**
 * Airbnb-style "every listing as a pin" map. Only properties with a location (dropped
 * through the dashboard's LocationPicker) can appear — older listings saved before that
 * existed just won't have one until their manager re-saves through the updated form.
 *
 * Classic google.maps.Marker can't render custom HTML content (just a plain-text
 * `label` on a pin), so price pills use OverlayView instead — a positioned div that
 * moves with the map, same idea AdvancedMarkerElement covers natively but without
 * needing a Cloud-configured mapId.
 */
const PropertiesMap = ({ properties, selectedId, onSelectProperty }) => {
  const { isLoaded } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });
  const [hoveredId, setHoveredId] = useState(null);

  const pinned = useMemo(() => properties.filter((p) => p.location), [properties]);

  const center = useMemo(() => {
    if (pinned.length === 0) return DEFAULT_CENTER;
    const lat = pinned.reduce((sum, p) => sum + p.location.latitude, 0) / pinned.length;
    const lng = pinned.reduce((sum, p) => sum + p.location.longitude, 0) / pinned.length;
    return { lat, lng };
  }, [pinned]);

  if (!isLoaded) {
    return (
      <div
        style={MAP_CONTAINER_STYLE}
        className="bg-gray-100 flex items-center justify-center text-gray-400 text-sm"
      >
        Loading map…
      </div>
    );
  }

  return (
    <div className="relative">
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={center}
        zoom={pinned.length > 0 ? 11 : 6}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
      >
        {pinned.map((property) => {
          const isActive = property.id === selectedId || property.id === hoveredId;
          return (
            <OverlayView
              key={`${property.managerId}__${property.id}`}
              position={{ lat: property.location.latitude, lng: property.location.longitude }}
              mapPaneName={OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={getPixelPositionOffset}
            >
              <button
                type="button"
                onClick={() => onSelectProperty(property)}
                onMouseOver={() => setHoveredId(property.id)}
                onMouseOut={() => setHoveredId(null)}
                className={`px-2.5 py-1.5 rounded-full text-xs font-bold shadow-md border whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white text-gray-900 border-gray-200 hover:border-navy'
                }`}
              >
                {formatMarkerPrice(property.rentAmount)}
              </button>
            </OverlayView>
          );
        })}
      </GoogleMap>

      {pinned.length === 0 && (
        <div className="absolute top-3 left-3 right-3 bg-navy/90 text-white text-sm rounded-lg px-4 py-2.5 flex items-center gap-2">
          <MapPin size={16} className="shrink-0" />
          None of these listings has a pin yet — check back once an agency adds one.
        </div>
      )}
    </div>
  );
};

export default PropertiesMap;
